import React, { Suspense, useCallback, useContext, useEffect, useState } from 'react';
import tw from 'twin.macro';
import { Link, useParams } from 'react-router-dom';
import { TitleRow } from '../text';
import { Filter, TopperSelection } from '../filter';
import { Main } from '../sections';
import { SideBar } from '../sidebars';
import { Loading } from '../loading';
import { ItemsGridList, ItemsList } from '../items';
import { AppContext, RouterContext } from '../context';
import { Carousel } from '../carousel';
import { SearchSorting } from '../sortings';
import { DropIcon } from 'icons';
import { formatDate, pageMetaTitle } from '../../lib/helpers';
import { Button } from '../button';
import { MetaTags } from '../meta';

const HistoryNavigation = ({ offset, period }) => {
  const isDay = period === 'dag';
  return (
    <div tw='flex gap-4'>
      {((offset >= 7 && isDay) || (!isDay && offset >= 1)) && (
        <Link to={`/nieuw/${period}/${offset - (isDay ? 7 : 1)}`}>
          <Button label='&#171; week' />
        </Link>
      )}
      {offset > 0 && isDay && (
        <Link to={`/nieuw/${period}/${offset - 1}`}>
          <Button label='&#8249; dag' />
        </Link>
      )}
      {offset < 365 && isDay && (
        <Link to={`/nieuw/${period}/${offset + 1}`}>
          <Button label='dag &#8250;' />
        </Link>
      )}
      {offset < 365 && (
        <Link to={`/nieuw/${period}/${offset + (isDay ? 7 : 1)}`}>
          <Button label='week &#187;' />
        </Link>
      )}
    </div>
  );
};

const historyDateTitle = ({ isHistory, offset = 0, period, date }) => {
  if (isHistory) {
    let date = '';
    if (offset === 0 || offset === undefined) {
      return 'vandaag';
    }
    if (period === 'dag') {
      if (offset % 7 === 0) {
        date = `${offset / 7} ${offset / 7 === 1 ? 'week' : 'weken'}`;
      }
      if (offset % 7 !== 0) {
        date = `${offset} ${offset === 1 ? 'dag' : 'dagen'}`;
      }
    }
    if (period === 'week') {
      date = `${offset} ${offset === 1 ? 'week' : 'weken'}`;
    }
    return `${date} geleden`;
  } else {
    const dateTitle = date && formatDate(date.split('-').reverse().join('-'));
    return dateTitle;
  }
};

const toppersRightBlock = () => (
  <div tw='flex justify-center items-center gap-2'>
    <TopperSelection />
    <Filter isRight />
  </div>
);
const activeTagRightBlock = () => (
  <div tw='flex justify-center items-center gap-2'>
    <SearchSorting onClick={() => window.scrollTo(0, 0)} />
    <Filter isRight />
  </div>
);

const componentsCache = {};

const SideBarBlock = () => {
  const { windowSize } = useContext(AppContext);
  const {
    pagePath: { isDumpertTv, isSearch, listType, activeTag },
  } = useContext(RouterContext);

  const [hideAds, setHideAds] = useState(false);

  useEffect(() => {
    setHideAds(isSearch || (activeTag && !isDumpertTv));
  }, []);

  useEffect(() => {
    setHideAds(isSearch || (activeTag && !isDumpertTv));
  }, [isSearch, activeTag, isDumpertTv]);

  return (
    <>
      {!['MOBILE'].includes(windowSize.adBreakPoint) && (
        <SideBar activeListType={listType} hideAds={hideAds} />
      )}
    </>
  );
};

export const ItemsListPage = () => {
  const params = useParams();
  const { date, period, offset } = params;
  const {
    listPages: { pageNums },
    activeList,
    windowSize,
  } = useContext(AppContext);
  const {
    pagePath: {
      isToppers,
      isDumpertTv,
      isSearch,
      isHistory,
      isDate,
      listType,
      activeTag,
      pageTitle,
    },
  } = useContext(RouterContext);
  const historyOffset = offset || 0;
  const hideAds = isSearch || (activeTag && !isDumpertTv);
  const { isModalActive } = useContext(RouterContext);

  const historyRightBlock = useCallback(
    (offset, period) => {
      const cacheKey = `historyRightBlock-${offset + period}`;
      if (!componentsCache[cacheKey]) {
        componentsCache[cacheKey] = () => (
          <HistoryNavigation offset={Number(offset)} period={period} />
        );
      }
      return componentsCache[cacheKey];
    },
    [offset, period],
  );

  const rightBlock = useCallback(
    (isHistory, isDate) => {
      const cacheKey = `rightBlock-${isHistory + isDate}`;
      if (!componentsCache[cacheKey]) {
        componentsCache[cacheKey] = () => <Filter showReload={!isHistory && !isDate} isRight />;
      }
      return componentsCache[cacheKey];
    },
    [isHistory, isDate],
  );

  return (
    <>
      {!isModalActive && <MetaTags title={pageMetaTitle(pageTitle !== 'nieuw' ? pageTitle : '')} />}
      <Main SideBar={SideBarBlock(listType, isSearch, activeTag, isDumpertTv)} showAds={!hideAds}>
        {listType === 'latest' &&
          ['MOBILE'].includes(windowSize.adBreakPoint) &&
          !(isHistory || isDate) && (
            <div tw='relative aspect-w-16 aspect-h-9 relative w-screen -ml-2'>
              <Suspense fallback={<Loading sliderItemPlaceholder />}>
                <ItemsList
                  page={0}
                  landscape={false}
                  listType='toppers'
                  period={period || ''}
                  WrapperType={Carousel}
                  maxNumItems={5}
                  isSidebar
                  hideAds
                />
                <div tw='absolute w-36 block top-0'>
                  <div tw='absolute left-1.5 text-dark z-10 uppercase font-black'>Top 5</div>
                  <DropIcon tw='absolute left-4 top-1 text-brand drop-shadow scale-125 scale-x-[200%]' />
                </div>
              </Suspense>
            </div>
          )}

        <div>
          <TitleRow
            RightBlock={
              isToppers
                ? toppersRightBlock
                : activeTag
                  ? activeTagRightBlock
                  : isHistory
                    ? historyRightBlock(offset, period)
                    : rightBlock(isHistory, isDate)
            }
            sticky
            css={[
              tw`bg-primary bg-opacity-80 backdrop-blur-sm z-30 md:mb-2`,
              isToppers && !!period && tw`bg-primary`,
            ]}
          >
            {!isHistory && !isDate && (
              <>
                {pageTitle} {period}
              </>
            )}
            {(isHistory || isDate) && (
              <>{historyDateTitle({ isHistory, offset: Number(offset), period, date })}</>
            )}
          </TitleRow>
          {pageNums[activeList] &&
            pageNums[activeList].map((item) => (
              <ItemsGridList
                key={item}
                listType={listType}
                currentPageNum={isHistory ? historyOffset : item}
                date={(isDate && date) || undefined}
                period={period}
                tag={activeTag}
                hideLoadMore={isHistory || isDate}
                hideAds={hideAds}
              />
            ))}
        </div>
      </Main>
    </>
  );
};
